import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { authAPI } from '../services/auth';
import { api, DeepAnalysisExportData, DeepAnalysisTimelineItem } from '../services/api';
import { useLayout } from '../context/LayoutContext';
import FeedbackForm, { FeedbackData } from '../components/FeedbackForm';
import { ConversationTimeline, FollowupInput, useConversationSession, DeepAnalysisExportMenu, RecentSessionsSection, ShareAnalysisDialog } from '../components/deep-analysis';
import { isReportItem, isUserInputItem, isClarificationRequestItem, isFollowupQuestionItem, isFollowupResponseItem } from '../types/conversation';
import CostEstimate from '../components/CostEstimate';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { useAuth } from '../context/AuthContext';

// Format case number (handle arrays and objects)
function formatCaseNumber(caseNumber: unknown, fallback = ''): string {
  if (!caseNumber) return fallback;
  if (typeof caseNumber === 'string') return caseNumber;
  if (Array.isArray(caseNumber)) {
    return caseNumber.map(cn => formatCaseNumber(cn, fallback)).join(', ');
  }
  if (typeof caseNumber === 'object') {
    // Try common fields
    const obj = caseNumber as Record<string, unknown>;
    if (obj.caseNumber) return formatCaseNumber(obj.caseNumber, fallback);
    if (obj.number) return String(obj.number);
    if (obj.signature) return String(obj.signature);
    // Try to extract any string value
    const values = Object.values(obj).filter(v => typeof v === 'string');
    if (values.length > 0) return values.join(' ');
  }
  return String(caseNumber);
}

// Format date to localized format
function formatDate(dateStr: string, locale = i18next.language === 'pl' ? 'pl-PL' : 'en-US'): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

interface ProgressStep {
  type: string;
  message: string;
  timestamp: string;
  completed: boolean;
}

interface AnalysisReport {
  thesis: string;
  supportiveJudgments: JudgmentSummary[];
  opposingJudgments: JudgmentSummary[];
  neutralJudgments: JudgmentSummary[];
  overallAssessment: string;
  recommendation: string;
  reportMarkdown: string;
}

interface JudgmentSummary {
  caseNumber: string;
  courtType: string;
  date: string;
  stance: string;
  keyQuote: string;
  reasoning: string;
}

// Analysis modes
type AnalysisMode = 'quick' | 'deep';

export default function AnalysisPage() {
  const { t } = useTranslation();
  const { setFullHeight } = useLayout();
  const { user } = useAuth();
  const emailVerified = !user || user.emailVerified;
  const [searchParams, setSearchParams] = useSearchParams();
  const [document, setDocument] = useState('');
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('quick');

  // Quick analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState<ProgressStep[]>([]);
  const [report, setReport] = useState<AnalysisReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(4);
  const [maxJudgments, setMaxJudgments] = useState(10);
  const searchType: 'hybrid' | 'semantic' = 'semantic';
  const eventSourceRef = useRef<EventSource | null>(null);

  // Deep analysis state - using the new hook
  const {
    session: deepSession,
    startAnalysis: startDeepAnalysis,
    submitClarification,
    sendFollowup,
    reset: resetDeepSession,
    cancelClarification,
    loadSession,
    isLoadingSession,
    loadError,
  } = useConversationSession({
    onError: (err) => setError(err),
  });

  const [personalDataAcknowledged, setPersonalDataAcknowledged] = useState(false);
  const [isSubmittingClarification, setIsSubmittingClarification] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);

  // Load session from URL param on mount
  useEffect(() => {
    const sessionId = searchParams.get('session');
    if (sessionId && !deepSession.id && !isLoadingSession) {
      setAnalysisMode('deep');
      setLoadingSessionId(sessionId);
      loadSession(sessionId).finally(() => {
        setLoadingSessionId(null);
        // Clear the URL param after loading
        setSearchParams({}, { replace: true });
      });
    }
  }, [searchParams, deepSession.id, isLoadingSession, loadSession, setSearchParams]);

  // Handle session selection from RecentSessionsSection
  const handleSessionSelect = useCallback((sessionId: string) => {
    setAnalysisMode('deep');
    setLoadingSessionId(sessionId);
    loadSession(sessionId).finally(() => {
      setLoadingSessionId(null);
    });
  }, [loadSession]);

  // Check if deep analysis has started (timeline has items)
  const deepAnalysisStarted = deepSession.timeline.length > 0;
  const deepAnalysisComplete = deepSession.status === 'complete';
  const isAwaitingClarification = deepSession.status === 'awaiting_clarification';
  const isDeepAnalyzing = deepSession.status === 'analyzing';

  // Toggle full-height layout mode when deep analysis is active
  useEffect(() => {
    const shouldBeFullHeight = analysisMode === 'deep' && deepAnalysisStarted;
    setFullHeight(shouldBeFullHeight);
    return () => setFullHeight(false);
  }, [analysisMode, deepAnalysisStarted, setFullHeight]);

  // Check if there's a streaming followup response
  const hasStreamingFollowup = deepSession.timeline.some(
    item => item.type === 'followup_response' && 'isStreaming' in item && item.isStreaming
  );

  // Handle deep analysis start
  const handleDeepAnalyze = useCallback(async () => {
    if (!emailVerified) {
      setError(t('auth.verifyEmail.analysisBlocked'));
      return;
    }
    if (!document.trim()) {
      setError(t('analysis.enterDocument'));
      return;
    }
    setError(null);
    await startDeepAnalysis(document, { personalDataAcknowledged });
  }, [document, startDeepAnalysis, personalDataAcknowledged, emailVerified, t]);

  // Handle clarification submit with loading state
  const handleClarificationSubmit = useCallback(async (responses: Parameters<typeof submitClarification>[0]) => {
    setIsSubmittingClarification(true);
    try {
      await submitClarification(responses);
    } finally {
      setIsSubmittingClarification(false);
    }
  }, [submitClarification]);

  // Quick analysis handlers
  const handleQuickAnalyze = useCallback(async () => {
    if (!emailVerified) {
      setError(t('auth.verifyEmail.analysisBlocked'));
      return;
    }
    if (!document.trim()) {
      setError(t('analysis.enterDocument'));
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setProgress([]);
    setReport(null);
    setCurrentStep(0);

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const accessToken = authAPI.getAccessToken();
      const response = await fetch('/api/v1/analysis/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          document,
          personalDataAcknowledged,
          options: {
            maxJudgments,
            searchType,
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('analysis.analysisError'));
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error(t('analysis.cannotReadResponse'));
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            continue;
          }
          if (line.startsWith('data:')) {
            const data = line.substring(5).trim();
            try {
              const parsed = JSON.parse(data);
              handleSSEEvent(parsed);
            } catch {
              // Ignore parse errors for partial data
            }
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('analysis.analysisError'));
      setIsAnalyzing(false);
    }
  }, [document, maxJudgments, searchType]);

  const handleSSEEvent = (event: { type?: string; step?: number; totalSteps?: number; message?: string; data?: Record<string, unknown>; error?: string; report?: AnalysisReport; status?: string }) => {
    if (event.error) {
      setError(event.error);
      return;
    }

    if (event.step !== undefined) {
      setCurrentStep(event.step);
    }
    if (event.totalSteps !== undefined) {
      setTotalSteps(event.totalSteps);
    }

    if (event.message) {
      setProgress((prev) => {
        const newProgress = [...prev];
        const lastStep = newProgress[newProgress.length - 1];
        if (lastStep && !lastStep.completed) {
          lastStep.completed = true;
        }
        newProgress.push({
          type: event.type || 'progress',
          message: event.message || '',
          timestamp: new Date().toISOString(),
          completed: false,
        });
        return newProgress;
      });
    }

    if (event.status === 'completed' && event.report) {
      setReport(event.report);
      setProgress((prev) => {
        const newProgress = [...prev];
        const lastStep = newProgress[newProgress.length - 1];
        if (lastStep) {
          lastStep.completed = true;
        }
        return newProgress;
      });
      setIsAnalyzing(false);
    }
  };

  // Handler for submitting feedback
  const handleFeedbackSubmit = useCallback(async (feedback: FeedbackData) => {
    if (!deepSession.id) return;

    setIsSubmittingFeedback(true);
    try {
      await api.submitFeedback(deepSession.id, {
        overallRating: feedback.overallRating,
        relevanceRating: feedback.relevanceRating,
        completenessRating: feedback.completenessRating,
        clarityRating: feedback.clarityRating,
        comment: feedback.comment,
        helpfulAspects: feedback.helpfulAspects,
        improvementSuggestions: feedback.improvementSuggestions,
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      // Don't show error to user - feedback is optional
    } finally {
      setIsSubmittingFeedback(false);
    }
  }, [deepSession.id]);

  const handleReset = () => {
    setDocument('');
    setProgress([]);
    setReport(null);
    setError(null);
    setCurrentStep(0);
    resetDeepSession();
    setShowFeedbackForm(false);
  };

  const stanceStyles = {
    supportive: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800',
    opposing: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800',
    neutral: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  };

  // Check if deep session has a report
  const deepHasReport = deepSession.timeline.some(item => isReportItem(item));

  // Prepare export data from session
  const exportData = useMemo((): DeepAnalysisExportData | null => {
    if (!deepSession.id || !deepHasReport) return null;

    // Find the original query from the first user input
    const userInput = deepSession.timeline.find(item => isUserInputItem(item));
    const originalQuery = userInput && isUserInputItem(userInput) ? userInput.content : '';

    // Convert timeline items to export format
    const exportTimeline: DeepAnalysisTimelineItem[] = [];

    for (const item of deepSession.timeline) {
      if (isUserInputItem(item)) {
        exportTimeline.push({
          type: 'user',
          content: item.content,
          timestamp: item.createdAt,
        });
      } else if (isClarificationRequestItem(item)) {
        // Add clarification questions
        const questionsText = item.questions
          .map((q, i) => `${i + 1}. ${q.question}`)
          .join('\n');
        exportTimeline.push({
          type: 'assistant',
          content: `${t('analysis.clarificationQuestions')}\n${questionsText}`,
          timestamp: item.createdAt,
        });

        // Add user responses if answered
        if (item.status === 'answered' && item.responses) {
          const responsesText = item.responses
            .map(r => `- ${r.customResponse || r.selectedOption || ''}`)
            .join('\n');
          exportTimeline.push({
            type: 'clarification',
            content: responsesText,
            timestamp: item.createdAt,
          });
        }
      } else if (isReportItem(item)) {
        exportTimeline.push({
          type: 'report',
          content: item.markdown,
          timestamp: item.createdAt,
        });
      } else if (isFollowupQuestionItem(item)) {
        exportTimeline.push({
          type: 'user',
          content: item.content,
          timestamp: item.createdAt,
        });
      } else if (isFollowupResponseItem(item)) {
        exportTimeline.push({
          type: 'assistant',
          content: item.content,
          timestamp: item.createdAt,
        });
      }
      // Skip progress_block items in export
    }

    return {
      sessionId: deepSession.id,
      createdAt: deepSession.timeline[0]?.createdAt || new Date().toISOString(),
      originalQuery,
      timeline: exportTimeline,
      tokensUsed: deepSession.tokensUsed,
    };
  }, [deepSession.id, deepSession.timeline, deepSession.tokensUsed, deepHasReport]);

  // Root container needs different layout when deep analysis is active
  const isDeepAnalysisView = analysisMode === 'deep' && deepAnalysisStarted;

  return (
    <div className={isDeepAnalysisView ? 'flex flex-col flex-1 min-h-0 w-full' : 'space-y-6'}>
      {/* Input section - hide when deep analysis starts */}
      {!(analysisMode === 'deep' && deepAnalysisStarted) && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            {t('analysis.title')}
          </h2>
          {!emailVerified && (
            <div className="mb-4">
              <EmailVerificationBanner />
            </div>
          )}
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {t('analysis.description')}
          </p>
          <textarea
            value={document}
            onChange={(e) => setDocument(e.target.value)}
            placeholder={t('analysis.placeholder')}
            className="w-full h-64 px-4 py-3 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            disabled={isAnalyzing}
          />
          {/* Analysis mode selector */}
          <div className="mt-4 p-4 bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-900/50 dark:to-blue-900/20 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('analysis.analysisMode')}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Quick mode */}
              <button
                type="button"
                onClick={() => setAnalysisMode('quick')}
                disabled={isAnalyzing}
                className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                  analysisMode === 'quick'
                    ? 'border-blue-500 bg-white dark:bg-slate-800'
                    : 'border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                {analysisMode === 'quick' && (
                  <div className="absolute top-2 right-2">
                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="font-semibold text-slate-900 dark:text-white">{t('analysis.quickMode')}</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('analysis.quickModeDesc')}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
                    {t('analysis.quickModeCredit')}
                  </span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {t('analysis.quickModeFor')}
                  </span>
                </div>
              </button>

              {/* Deep mode */}
              <button
                type="button"
                onClick={() => setAnalysisMode('deep')}
                disabled={isAnalyzing}
                className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                  analysisMode === 'deep'
                    ? 'border-purple-500 bg-white dark:bg-slate-800'
                    : 'border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                {analysisMode === 'deep' && (
                  <div className="absolute top-2 right-2">
                    <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="font-semibold text-slate-900 dark:text-white">{t('analysis.deepMode')}</span>
                  <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded font-medium">
                    {t('analysis.deepModeNew')}
                  </span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('analysis.deepModeDesc')}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                    {t('analysis.deepModeCredit')}
                  </span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {t('analysis.deepModeFor')}
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Quick mode options */}
          {analysisMode === 'quick' && (
            <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
              <div className="flex flex-wrap items-center gap-6">
                {/* Max judgments */}
                <div className="flex items-center gap-3">
                  <label className="text-sm text-slate-600 dark:text-slate-400">
                    {t('analysis.judgmentCount')}
                  </label>
                  <select
                    value={maxJudgments}
                    onChange={(e) => setMaxJudgments(Number(e.target.value))}
                    disabled={isAnalyzing}
                    className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={5}>{t('analysis.judgmentOption5')}</option>
                    <option value={10}>{t('analysis.judgmentOption10')}</option>
                    <option value={15}>{t('analysis.judgmentOption15')}</option>
                    <option value={20}>{t('analysis.judgmentOption20')}</option>
                    <option value={30}>{t('analysis.judgmentOption30')}</option>
                  </select>
                </div>

              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">
                {t('analysis.semanticHint')}
              </p>
            </div>
          )}

          {/* Deep mode info */}
          {analysisMode === 'deep' && (
            <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="font-medium text-purple-900 dark:text-purple-100">{t('analysis.deepHowTitle')}</h4>
                  <ul className="mt-2 space-y-1 text-sm text-purple-800 dark:text-purple-200">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                      {t('analysis.deepHow1')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                      {t('analysis.deepHow2')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                      {t('analysis.deepHow3')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                      {t('analysis.deepHow4')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Personal data anonymization disclaimer */}
          <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="flex-1">
                <h4 className="font-medium text-amber-900 dark:text-amber-100 text-sm">{t('analysis.anonymization.title')}</h4>
                <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">{t('analysis.anonymization.description')}</p>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={personalDataAcknowledged}
                    onChange={(e) => setPersonalDataAcknowledged(e.target.checked)}
                    className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                  />
                  <span className="text-xs text-amber-900 dark:text-amber-100 font-medium">{t('analysis.anonymization.acknowledge')}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Cost estimation */}
          <CostEstimate documentLength={document.length} mode={analysisMode} />

          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {t('analysis.charCount', { count: document.length })}
            </span>
            <button
              onClick={analysisMode === 'deep' ? handleDeepAnalyze : handleQuickAnalyze}
              disabled={isAnalyzing || isDeepAnalyzing || !document.trim() || !personalDataAcknowledged}
              className={`px-6 py-2.5 ${
                analysisMode === 'deep'
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              } disabled:bg-slate-400 text-white font-medium rounded-lg transition-colors flex items-center gap-2`}
            >
              {(isAnalyzing || isDeepAnalyzing) ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {analysisMode === 'deep' ? t('analysis.inProgress') : t('analysis.analyzing')}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  {analysisMode === 'deep' ? t('analysis.startDeep') : t('analysis.analyze')}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Recent Sessions - Show only when deep mode selected and no analysis started */}
      {analysisMode === 'deep' && !deepAnalysisStarted && !isLoadingSession && (
        <RecentSessionsSection
          onSessionSelect={handleSessionSelect}
          loadingSessionId={loadingSessionId}
          maxSessions={3}
        />
      )}

      {/* Loading indicator for session restoration */}
      {isLoadingSession && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <svg className="w-8 h-8 text-purple-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-slate-600 dark:text-slate-400">{t('analysis.loadingSession')}</p>
          </div>
        </div>
      )}

      {/* Deep Analysis - New Timeline View */}
      {analysisMode === 'deep' && deepAnalysisStarted && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1 min-h-0 w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Back to history link */}
              <Link
                to="/analysis/history"
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                title={t('analysis.historyLink')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded font-medium">
                  {t('analysis.deepAnalysisLabel')}
                </span>
                {t('analysis.conversation')}
              </h2>
            </div>
            {/* Show actions when analysis is complete */}
            {deepAnalysisComplete && (
              <div className="flex items-center gap-2">
                {/* Share button */}
                {deepSession.id && (
                  <button
                    onClick={() => setShowShareDialog(true)}
                    className="px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 border border-purple-300 dark:border-purple-600 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    {t('analysis.share')}
                  </button>
                )}

                {/* Export menu */}
                {exportData && <DeepAnalysisExportMenu exportData={exportData} />}

                {/* New analysis button */}
                <button
                  onClick={handleReset}
                  className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  {t('analysis.newAnalysis')}
                </button>
              </div>
            )}
          </div>

          {/* Timeline */}
          <ConversationTimeline
            timeline={deepSession.timeline}
            onClarificationSubmit={handleClarificationSubmit}
            onClarificationCancel={cancelClarification}
            isSubmittingClarification={isSubmittingClarification}
          />

          {/* Followup Input - only show when complete and not awaiting clarification */}
          {deepAnalysisComplete && !isAwaitingClarification && (
            <FollowupInput
              onSend={sendFollowup}
              tokensUsed={deepSession.tokensUsed}
              tokensLimit={deepSession.tokensLimit}
              disabled={isDeepAnalyzing}
              isStreaming={hasStreamingFollowup}
            />
          )}
        </div>
      )}

      {/* Quick Analysis - Progress during analysis */}
      {analysisMode === 'quick' && (isAnalyzing || progress.length > 0) && !report && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('analysis.progress')}
            </h3>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {t('analysis.step', { current: currentStep, total: totalSteps })}
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 mb-4">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <div className="space-y-2">
            {progress.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                {step.completed ? (
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                <span className={`text-sm ${step.completed ? 'text-slate-600 dark:text-slate-400' : 'text-slate-900 dark:text-white font-medium'}`}>
                  {step.message}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error section */}
      {(error || deepSession.error || loadError) && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error || deepSession.error || loadError}</span>
          </div>
        </div>
      )}

      {/* Report section - Quick analysis only */}
      {report && analysisMode === 'quick' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {t('analysis.report')}
              </h2>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('analysis.newAnalysis')}
              </button>
            </div>

            {/* Quick analysis - structured view */}
            <div className="prose prose-slate dark:prose-invert max-w-none">
              <h3>{t('analysis.thesis')}</h3>
              <p className="text-slate-700 dark:text-slate-300">{report.thesis}</p>
            </div>
          </div>

          {/* Structured sections for quick analysis */}
          <>
            {/* Summary stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg border ${stanceStyles.supportive}`}>
                <div className="text-2xl font-bold">{report.supportiveJudgments.length}</div>
                <div className="text-sm">{t('analysis.supportive')}</div>
              </div>
              <div className={`p-4 rounded-lg border ${stanceStyles.opposing}`}>
                <div className="text-2xl font-bold">{report.opposingJudgments.length}</div>
                <div className="text-sm">{t('analysis.opposing')}</div>
              </div>
              <div className={`p-4 rounded-lg border ${stanceStyles.neutral}`}>
                <div className="text-2xl font-bold">{report.neutralJudgments.length}</div>
                <div className="text-sm">{t('analysis.neutral')}</div>
              </div>
            </div>

            {/* Overall assessment */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                {t('analysis.overallAssessment')}
              </h3>
              <p className="text-blue-800 dark:text-blue-200">{report.overallAssessment}</p>
            </div>

            {/* Supportive judgments */}
            {report.supportiveJudgments.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-green-700 dark:text-green-400 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('analysis.supportive')} ({report.supportiveJudgments.length})
                </h3>
                <div className="space-y-4">
                  {report.supportiveJudgments.map((judgment, index) => (
                    <JudgmentCard key={index} judgment={judgment} stance="supportive" />
                  ))}
                </div>
              </div>
            )}

            {/* Opposing judgments */}
            {report.opposingJudgments.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('analysis.opposing')} ({report.opposingJudgments.length})
                </h3>
                <div className="space-y-4">
                  {report.opposingJudgments.map((judgment, index) => (
                    <JudgmentCard key={index} judgment={judgment} stance="opposing" />
                  ))}
                </div>
              </div>
            )}

            {/* Neutral judgments */}
            {report.neutralJudgments.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                  {t('analysis.neutral')} ({report.neutralJudgments.length})
                </h3>
                <div className="space-y-4">
                  {report.neutralJudgments.map((judgment, index) => (
                    <JudgmentCard key={index} judgment={judgment} stance="neutral" />
                  ))}
                </div>
              </div>
            )}

            {/* Recommendation */}
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-6">
              <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('analysis.recommendation')}
              </h3>
              <p className="text-amber-800 dark:text-amber-200">{report.recommendation}</p>
            </div>
          </>
        </div>
      )}

      {/* Rating FAB for deep analysis */}
      {deepHasReport && analysisMode === 'deep' && deepSession.id && (
        <button
          onClick={() => setShowFeedbackForm(true)}
          className="fixed bottom-6 right-6 z-30 p-4 bg-amber-500 text-white rounded-full shadow-lg hover:bg-amber-600 transition-colors"
          title={t('analysis.rateAnalysis')}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </button>
      )}

      {/* Feedback Modal for deep analysis */}
      {showFeedbackForm && analysisMode === 'deep' && deepSession.id && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <FeedbackForm
              sessionId={deepSession.id}
              onSubmit={handleFeedbackSubmit}
              onClose={() => setShowFeedbackForm(false)}
              isSubmitting={isSubmittingFeedback}
            />
          </div>
        </div>
      )}

      {/* Share Dialog for deep analysis */}
      {showShareDialog && analysisMode === 'deep' && deepSession.id && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <ShareAnalysisDialog
              sessionId={deepSession.id}
              onClose={() => setShowShareDialog(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function JudgmentCard({ judgment, stance }: { judgment: JudgmentSummary; stance: 'supportive' | 'opposing' | 'neutral' }) {
  const { t, i18n } = useTranslation();
  const borderColors = {
    supportive: 'border-l-green-500',
    opposing: 'border-l-red-500',
    neutral: 'border-l-slate-400',
  };

  const formattedCaseNumber = formatCaseNumber(judgment.caseNumber, t('analysis.noCaseNumber'));
  const formattedCourtType = judgment.courtType ? (t('courtType.' + judgment.courtType, { defaultValue: judgment.courtType })) : '';
  const formattedDate = formatDate(judgment.date, i18n.language);

  return (
    <div className={`border-l-4 ${borderColors[stance]} pl-4 py-2`}>
      <div className="flex items-center flex-wrap gap-2 mb-2">
        <span className="font-semibold text-slate-900 dark:text-white">{formattedCaseNumber}</span>
        {formattedCourtType && (
          <span className="text-sm px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded">
            {formattedCourtType}
          </span>
        )}
        {formattedDate && (
          <span className="text-sm text-slate-400 dark:text-slate-500">{formattedDate}</span>
        )}
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{judgment.reasoning}</p>
      {judgment.keyQuote && (
        <blockquote className="text-sm italic text-slate-500 dark:text-slate-400 border-l-2 border-slate-300 dark:border-slate-600 pl-3">
          "{judgment.keyQuote}"
        </blockquote>
      )}
    </div>
  );
}
